<script>
  import { onMount } from "svelte"
  import Carto from "./Carto.svelte"
  import Resizer from "$lib/components/guardian/Resizer.svelte"
  import { getJson } from "$lib/helpers/guardian/toolbelt.js"
  import { parseAbcFeed, getCurrentPosition } from "$lib/helpers/parseAbcFeed.js"

  const defaultGeoJsonStylesABC = [
    // Hazard polygons - fill first (underneath), exclude UNK (possible range has no fill)
    {
      id: "hazard-fill",
      type: "fill",
      source: "overlays",
      minzoom: 0,
      filter: ["all", ["==", "$type", "Polygon"], ["!=", "urgency", "UNK"]],
      paint: {
        "fill-color": [
          "match",
          ["get", "urgency"],
          "WARN",
          "#998ec3",
          "WATCH",
          "#542788",
          "#1e88e5",
        ],
        "fill-opacity": 0.5,
      },
    },
    // Hazard polygons - outline (exclude UNK, which uses dashed boundary)
    {
      id: "hazard-outline",
      type: "line",
      source: "overlays",
      minzoom: 0,
      filter: ["all", ["==", "$type", "Polygon"], ["!=", "urgency", "UNK"]],
      paint: {
        "line-color": [
          "match",
          ["get", "urgency"],
          "WARN",
          "#998ec3",
          "WATCH",
          "#542788",
          "#1e88e5",
        ],
        "line-width": 0,
      },
    },
    // UNK hazard (possible cyclone range) - grey diagonal line fill
    {
      id: "hazard-unk-fill",
      type: "fill",
      source: "overlays",
      minzoom: 0,
      filter: ["all", ["==", "$type", "Polygon"], ["==", "urgency", "UNK"]],
      paint: {
        "fill-pattern": "diagonal-lines-grey",
        "fill-opacity": 0.3,
      },
    },
    // UNK hazard boundary - grey outline
    {
      id: "hazard-unk-boundary",
      type: "line",
      source: "overlays",
      minzoom: 0,
      filter: ["all", ["==", "$type", "LineString"], ["==", "hazard_boundary", true]],
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": "#888",
        "line-width": 1,
      },
    },
    // Historic track path (grey)
    {
      id: "geojson-historic-track",
      type: "line",
      source: "overlays",
      minzoom: 0,
      filter: ["all", ["==", "$type", "LineString"], ["==", "tracktype", "Historic"]],
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": "#888",
        "line-width": 3,
      },
    },
    // Connector line (historic to forecast)
    {
      id: "geojson-connector-track",
      type: "line",
      source: "overlays",
      minzoom: 0,
      filter: ["all", ["==", "$type", "LineString"], ["==", "tracktype", "Connector"]],
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": "#888",
        "line-width": 3,
        "line-dasharray": [2, 2],
      },
    },
    // Track path (LineString)
    {
      id: "geojson-track",
      type: "line",
      source: "overlays",
      minzoom: 0,
      filter: ["all", ["==", "$type", "LineString"], ["==", "tracktype", "Forecast"]],
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": "#c70000",
        "line-width": 4,
      },
    },
    // Historic points - white fill, stroke by category
    {
      id: "geojson-historic-points",
      type: "circle",
      source: "overlays",
      minzoom: 0,
      filter: ["all", ["==", "$type", "Point"], ["==", "fixtype", "Historic"], ["!=", "label_type", "place"]],
      paint: {
        "circle-radius": 8,
        "circle-color": "#fff",
        "circle-stroke-width": 2,
        "circle-stroke-color": [
          "match",
          ["get", "category_name"],
          "1",
          "#fef0d9",
          "2",
          "#fdcc8a",
          "3",
          "#fc8d59",
          "4",
          "#e34a33",
          "5",
          "#b30000",
          "#999",
        ],
      },
    },
    // Cyclone fix points (forecast only) - color by category_name
    {
      id: "geojson-points",
      type: "circle",
      source: "overlays",
      minzoom: 0,
      filter: ["all", ["==", "$type", "Point"], ["!=", "fixtype", "Historic"], ["!=", "label_type", "place"]],
      paint: {
        "circle-radius": 8,
        "circle-color": [
          "match",
          ["get", "category_name"],
          "1",
          "#fef0d9",
          "2",
          "#fdcc8a",
          "3",
          "#fc8d59",
          "4",
          "#e34a33",
          "5",
          "#b30000",
          "#999",
        ],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#000",
      },
    },
    // Current position - larger, on top
    {
      id: "geojson-current-point",
      type: "circle",
      source: "overlays",
      minzoom: 0,
      filter: [
        "all",
        ["==", "$type", "Point"],
        ["==", "fixtype", "Current"],
        ["!=", "label_type", "place"],
      ],
      paint: {
        "circle-radius": 12,
        "circle-color": [
          "match",
          ["get", "category_name"],
          "1",
          "#fef0d9",
          "2",
          "#fdcc8a",
          "3",
          "#fc8d59",
          "4",
          "#e34a33",
          "5",
          "#b30000",
          "#999",
        ],
        "circle-stroke-width": 1,
        "circle-stroke-color": "#000",
      },
    },
    // Category label inside point circles (forecast + historic)
    {
      id: "geojson-point-labels",
      type: "symbol",
      source: "overlays",
      minzoom: 0,
      filter: ["all", ["==", "$type", "Point"], ["!=", "label_type", "place"]],
      layout: {
        "text-field": ["to-string", ["get", "category_name"]],
        "text-size": 11,
        "text-anchor": "center",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": [
          "match",
          ["get", "fixtype"],
          "Historic",
          "#333",
          [
            "match",
            ["get", "category_name"],
            "1",
            "#333",
            "2",
            "#333",
            "3",
            "#ffffff",
            "4",
            "#ffffff",
            "5",
            "#ffffff",
            "#ffffff",
          ],
        ],
        "text-halo-color": [
          "match",
          ["get", "fixtype"],
          "Historic",
          "rgba(255,255,255,0.9)",
          [
            "match",
            ["get", "category_name"],
            "1",
            "rgba(255,255,255,0.9)",
            "2",
            "rgba(255,255,255,0.9)",
            "rgba(0,0,0,0.8)",
          ],
        ],
        "text-halo-width": 1,
      },
    },
    // Place labels
    {
      id: "place-labels",
      type: "symbol",
      source: "overlays",
      minzoom: 0,
      filter: ["==", "label_type", "place"],
      layout: {
        "text-field": ["get", "label"],
        "text-size": 12,
        "text-anchor": "center",
        "text-offset": [0, 0],
        "text-allow-overlap": false,
      },
      paint: {
        "text-color": "#333",
        "text-halo-color": "#fff",
        "text-halo-width": 1.5,
      },
    },
  ]

  const placeLabels = [
    { label: "Coen", lng: 143.20137091280495, lat: -13.938527196864657 },
  ]

  let cycloneData = $state(null);
  let currentPosition = $state(null);

  let {
    name,
    feedUrl = "https://interactive.guim.co.uk/26/03/19-oz-narelle-cyclone/cyclone-warning-tracker-map.json",
    geoJsonStyles = defaultGeoJsonStylesABC,
  } = $props();

  onMount(async () => {
    try {
      const feed = await getJson(feedUrl);
      if (feed && (feed.forecast_points || feed.hazards || feed.historic)) {
        const parsed = parseAbcFeed(feed);
        const labelFeatures = placeLabels.map(({ label, lng, lat }) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [lng, lat] },
          properties: { label, label_type: "place" },
        }));
        cycloneData = {
          ...parsed,
          features: [...parsed.features, ...labelFeatures],
        };
        currentPosition = getCurrentPosition(feed);
      }
    } catch (e) {
      console.error("Could not load cyclone data", e);
    }
  });


</script>

<Resizer atomName="#{name}" />

{#if cycloneData}
<Carto
  geojson={cycloneData}
  geoJsonStyles={geoJsonStyles}
  currentPosition={currentPosition}
  showTimeLegend={true}
  MAP_INTERACTIVE={true}
  center={[145, -14]}
  zoom={5}
  headline={"Tropical Cyclone Narelle tracking map"}
  subtitle={(() => {
    const issueTime = cycloneData.properties?.issue_time_utc;
    let timeStr = "—";
    if (issueTime) {
      const diffMs = Date.now() - new Date(issueTime).getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      const diffDays = Math.floor(diffHours / 24);
      if (diffMins < 1) timeStr = "just now";
      else if (diffMins < 60) timeStr = `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
      else if (diffHours < 24) {
        timeStr = mins > 0
          ? `${diffHours} hour${diffHours === 1 ? "" : "s"} ${mins} minute${mins === 1 ? "" : "s"} ago`
          : `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
      } else if (diffDays < 7) {
        timeStr = `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
      } else {
        timeStr = new Date(issueTime).toLocaleDateString(undefined, {
          day: "numeric", month: "long", year: "numeric",
          hour: "numeric", minute: "2-digit", timeZoneName: "short",
        });
      }
    }
    return `Showing the forecast path and intensity of Cyclone Narelle. This is not a live map and should not be used in the case of an emergency - refer to the <a href='https://www.bom.gov.au/weather-and-climate/specialised-forecasts-and-observations/tropical-cyclone' target="_blank">Bureau of Meteorology for official warnings</a>. Last updated ${timeStr}.`;
  })()}
  source="Guardian graphic."
/>
{/if}

<style lang="scss">
</style>
